import { createContext, useContext, useReducer, PropsWithChildren } from 'react';
import {
  Customer,
  OnboardedStatus,
  LoyaltyData,
  VoucherList,
  Offers,
  CarouselOffers,
  SurveyQuestions,
  SurveyAnswers,
  ReferralCode,
  Transactions,
  BeforeInstallPromptEvent,
} from 'models';

export type StoreActions =
  | 'setOnboardedStatus'
  | 'setUserData'
  | 'setVouchersData'
  | 'setOffersData'
  | 'setLoyaltyData'
  | 'setOffersCarouselData'
  | 'setSurveyQuestions'
  | 'setSurveyAnswers'
  | 'setReferralCode'
  | 'setTransactions'
  | 'setInstallationPrompt';

interface Store {
  onboardedStatus?: OnboardedStatus;
  userData?: Customer;
  vouchersData?: VoucherList;
  offersData?: Offers;
  loyaltyData?: LoyaltyData;
  offersCarouselData?: CarouselOffers;
  surveyQuestions?: SurveyQuestions;
  surveyAnswers?: SurveyAnswers;
  referralCode?: ReferralCode;
  transactions?: Transactions;
  installationPrompt?: BeforeInstallPromptEvent | null;
}

interface Action {
  type: StoreActions;
  payload: Store;
}

type Dispatch = (action: Action) => void;

export interface StoreProps {
  store: Store;
  dispatch: Dispatch;
}

const initialStore: Store = {};

const AppStore = createContext<StoreProps>({
  store: initialStore,
  dispatch: () => {},
});

const { Provider } = AppStore;

const storeReducer = (store: Store, action: Action): Store => {
  const { type, payload } = action;
  const {
    onboardedStatus,
    userData,
    vouchersData,
    offersData,
    loyaltyData,
    offersCarouselData,
    surveyQuestions,
    surveyAnswers,
    referralCode,
    transactions,
    installationPrompt,
  } = payload;

  const updatedStore = {
    setOnboardedStatus: { ...store, onboardedStatus },
    setUserData: { ...store, userData },
    setVouchersData: { ...store, vouchersData },
    setOffersData: { ...store, offersData },
    setLoyaltyData: { ...store, loyaltyData },
    setOffersCarouselData: { ...store, offersCarouselData },
    setSurveyQuestions: { ...store, surveyQuestions },
    setSurveyAnswers: { ...store, surveyAnswers },
    setReferralCode: { ...store, referralCode },
    setTransactions: { ...store, transactions },
    setInstallationPrompt: { ...store, installationPrompt },
  };

  return updatedStore[type];
};

const StoreProvider = ({ children }: PropsWithChildren) => {
  const [store, dispatch] = useReducer(storeReducer, initialStore);
  return <Provider value={{ store, dispatch }}>{children}</Provider>;
};

const useStore = () => useContext(AppStore);

export { StoreProvider, useStore };
